import React from 'react';
import type { TitleSubtitleComponent as TitleSubtitleComponentType } from '../schemas/validation';
import { ButtonLink } from './shared/ButtonLink';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getThemeStyles } from '../styles/themes';
import { getFontFamily } from '../utils/fontLoader';

interface TitleSubtitleComponentProps {
  component: TitleSubtitleComponentType;
}

export const TitleSubtitleComponent: React.FC<TitleSubtitleComponentProps> = ({ component }) => {
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const brandFonts = useBrandFonts();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);
  const { data } = component;

  // Font styles for headings and body text
  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  // Spacing map
  const spacingMap = {
    tight: '0.5rem',
    normal: '1rem',
    loose: '2rem',
  };

  // Font weight map
  const fontWeightMap = {
    light: '300',
    normal: '400',
    bold: '700',
  };

  // Alignment map
  const alignmentMap = {
    left: 'left',
    center: 'center',
    right: 'right',
  };

  // Alignment classes for flex container
  const alignmentClassMap = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  };

  // Get title and subtitle tags
  const TitleTag = data.titleTag;
  const SubtitleTag = data.subtitleTag;

  // Don't render anything if both title and subtitle are empty
  if (!data.title && !data.subtitle) {
    return null;
  }

  // Color priority: customColors (user explicit) > theme/brand package > stored data defaults
  const titleColor =
    (data as any).customColors?.title || themeStyles.titleSubtitle?.titleColor || data.titleColor;
  const subtitleColor =
    (data as any).customColors?.subtitle ||
    themeStyles.titleSubtitle?.subtitleColor ||
    data.subtitleColor;
  const backgroundColor =
    (data as any).customColors?.background ||
    themeStyles.titleSubtitle?.background ||
    data.backgroundColor;

  // Button colors
  const buttonBgColor =
    (data as any).customColors?.buttonBg || themeStyles.titleSubtitle?.buttonBg || '#3b82f6';
  const buttonTextColor =
    (data as any).customColors?.buttonText || themeStyles.titleSubtitle?.buttonText || '#ffffff';

  // Get button styles based on button type
  const getButtonStyles = (style: string) => {
    switch (style) {
      case 'primary':
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
      case 'secondary':
        return {
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      case 'ghost':
        return {
          backgroundColor: 'transparent',
          color: buttonBgColor,
          border: 'none',
        };
      default:
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
    }
  };

  // Default font size classes if not specified
  const getTitleSizeClass = () => {
    if (data.titleFontSize) return ''; // User specified custom size
    // Default sizes based on heading tag
    const sizeMap: Record<string, string> = {
      h1: 'text-4xl',
      h2: 'text-3xl',
      h3: 'text-2xl',
      h4: 'text-xl',
      h5: 'text-lg',
      h6: 'text-base',
    };
    return sizeMap[data.titleTag] || 'text-3xl';
  };

  const getSubtitleSizeClass = () => {
    if (data.subtitleFontSize) return ''; // User specified custom size
    return 'text-xl'; // Default subtitle size matching team component
  };

  // Determine button alignment - use title alignment as default
  const buttonAlignment = data.titleAlignment || 'center';

  // Check if button should be rendered
  const button = (data as any).button;
  const showButton =
    button?.enabled && button?.text && button?.url !== undefined && button?.url !== null;

  return (
    <section className="py-8 px-4" style={{ backgroundColor }}>
      <div className="max-w-7xl mx-auto">
        {/* Title */}
        {data.title && (
          <TitleTag
            className={getTitleSizeClass()}
            style={{
              textAlign: alignmentMap[data.titleAlignment] as any,
              color: titleColor,
              fontSize: data.titleFontSize,
              fontWeight: fontWeightMap[data.titleFontWeight],
              marginTop: 0,
              marginLeft: 0,
              marginRight: 0,
              marginBottom: data.subtitle || showButton ? spacingMap[data.spacing] : 0,
              padding: 0,
              ...headingFontStyle,
            }}
          >
            {data.title}
          </TitleTag>
        )}

        {/* Subtitle */}
        {data.subtitle && (
          <>
            <style>{`
              .title-subtitle-content-${component.id}, .title-subtitle-content-${component.id} * {
                color: ${subtitleColor} !important;
              }
              .title-subtitle-content-${component.id} a {
                text-decoration: underline;
              }
            `}</style>
            <SubtitleTag
              className={`title-subtitle-content-${component.id} rich-text-content ${getSubtitleSizeClass()}`}
              style={{
                textAlign: alignmentMap[data.subtitleAlignment] as any,
                color: subtitleColor,
                fontSize: data.subtitleFontSize,
                fontWeight: fontWeightMap[data.subtitleFontWeight],
                margin: 0,
                marginBottom: showButton ? spacingMap[data.spacing] : 0,
                padding: 0,
                ...bodyFontStyle,
              }}
              dangerouslySetInnerHTML={{ __html: data.subtitle }}
            />
          </>
        )}

        {/* Button */}
        {showButton && (
          <div className={`flex ${alignmentClassMap[buttonAlignment]} mt-4`}>
            <ButtonLink
              url={button.url || '/'}
              target={button.target}
              className="px-6 py-3 text-base font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg inline-block text-center"
              style={getButtonStyles(button.style || 'primary')}
            >
              {button.text}
            </ButtonLink>
          </div>
        )}
      </div>
    </section>
  );
};
